import { HubConnectionState } from '@microsoft/signalr';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { Stack } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { PaymentCurrencyConversionSection } from 'src/modals/SaleDetails/components/PaymentCurrencyConversionSection';
import { PaymentSection } from 'src/modals/SaleDetails/components/PaymentSection/PaymentSection';
import { DeliverySection } from 'src/modals/SaleDetailsV2/components/DeliverySection';
import { FulfillmentSection } from 'src/modals/SaleDetailsV2/components/FulfillmentSection';
import { ContentId } from 'src/utils/constants/contentId';
import { getSaleDetailsUpdateInput } from 'src/utils/saleUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  Feature,
  Sale,
  SaleDetails,
  SaleInput,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';

export type BulkEditSalesSettingsDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  sales?: Sale[] | null;
  event?: Event;
  onOkay: (
    saleInput: SaleInput | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

export const BulkEditSalesSettingsDialog = ({
  sales,
  ...rest
}: BulkEditSalesSettingsDialogProps) => {
  const methods = useForm<SaleInput>({
    defaultValues: getSaleDetailsUpdateInput(sales?.[0] as SaleDetails),
  });

  return (
    <FormProvider {...methods}>
      <BulkEditSalesSettingsDialogContent
        sales={sales}
        {...rest}
        {...methods}
      />
    </FormProvider>
  );
};

function BulkEditSalesSettingsDialogContent({
  updateKey,
  sales,
  event,
  onOkay,
  onCancel,
  handleSubmit,
  watch,
  reset,
  formState,
  ...rest
}: BulkEditSalesSettingsDialogProps &
  Omit<ComponentProps<typeof FormProvider<SaleInput, unknown>>, 'children'>) {
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const [isLoading, setIsLoading] = useState(false);
  const input = watch();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await refreshExpandedListItems(true);
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
      setMainDialogOpened(false);
    },
    [
      onCancel,
      refreshExpandedListItems,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    ActionOutboxEntityType.Sale,
    BulkActionType.UpdateGeneralSettings,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(null, false, onPreviewReceived);
      } else {
        handleSubmit((i) =>
          onOkay(i, hasBackgroundBulkEditFeature, undefined, preview)
        )();
      }
      setIsLoading(false);
    },
    [onOkay, handleSubmit, preview, hasBackgroundBulkEditFeature]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  const hidePaymentCurrencyConversion = useUserHasFeature(
    Feature.HideSalePaymentConversionSection
  );

  const isInputValid = useMemo(() => {
    return !isEqual(input, formState.defaultValues ?? {});
  }, [formState.defaultValues, input]);

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <GenericDialog
        {...rest}
        size={stage === BulkEditStage.Preview ? 'xl' : 'lg'}
        header={
          <BulkEditHeader
            headerText={<Content id={ContentId.SaleSettings} />}
          />
        }
        onOpened={() => {
          reset(getSaleDetailsUpdateInput());
          initJob();
        }}
        onClosed={() => {
          setMainDialogOpened(false);
          if (progress) {
            setSelectionMode(undefined);
          }
          // Call the outside one if there is one
          rest.onClosed?.();
        }}
        onKeyUp={(e) => {
          if (submittButtonRef.current && e.key === 'Enter' && isInputValid) {
            submittButtonRef.current.click();
          }
        }}
        footer={
          <BulkEditFooter
            entityType={ActionOutboxEntityType.Sale}
            isLoading={isLoading}
            hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
            onClose={onClose}
            onSubmit={onSubmitHandler}
            disabled={!isInputValid}
            submittButtonRef={submittButtonRef}
          />
        }
        onCancel={isLoading ? undefined : onCancel}
      >
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Sale}
          isLoading={isRefreshing || isLoading}
          updateKey={updateKey}
        >
          <Stack direction="column" width="full" gap="xxl">
            <DeliverySection
              sale={sales?.[0] as SaleDetails}
              isBulkEdit
              disabled={isLoading}
            />

            <FulfillmentSection isBulkEdit />
            <PaymentSection isBulkEdit />
            {!hidePaymentCurrencyConversion && (
              <PaymentCurrencyConversionSection />
            )}
          </Stack>
        </BulkEditStatus>
      </GenericDialog>
    </>
  );
}
